import React from 'react';
import { Link, useLocation } from '@reach/router';

/**
 * Rutas que SÍ están servidas por la SPA de React (ver App.jsx y
 * patch/dkan_js_frontend.config.yml). Solo estas pueden navegarse con <Link>
 * de @reach/router sin recargar la página. Cualquier otra ruta interna
 * (p. ej. /historias, /user/login) la sirve Drupal y debe usar <a href> para
 * que el navegador haga la petición real al backend.
 */
const SPA_ROUTE_PREFIXES = [
  '/search',
  '/dataset/',
  '/resource/',
  '/api',
  '/publishers',
];

/**
 * Determina si una URL es externa (otro dominio, mailto:, tel:, etc.) o un
 * ancla, en cuyo caso nunca debe usarse <Link>.
 */
function isExternal(url) {
  if (!url || typeof url !== 'string') {
    return true;
  }
  return (
    /^https?:\/\//i.test(url) ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:') ||
    url.startsWith('#')
  );
}

/**
 * Indica si una ruta interna pertenece a la SPA de React.
 */
function isSpaRoute(url) {
  // Normaliza la home: "/" es la ruta raíz de la SPA.
  if (url === '/' || url === '') {
    return true;
  }
  return SPA_ROUTE_PREFIXES.some((prefix) =>
    prefix.endsWith('/')
      ? url.startsWith(prefix)
      : url === prefix || url.startsWith(prefix + '?') || url.startsWith(prefix + '/')
  );
}

/**
 * Devuelve el pathname (sin query) de una URL interna.
 */
function pathnameOf(url) {
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
}

/**
 * Enlace que decide automáticamente entre navegación SPA (<Link>, sin recarga)
 * y navegación tradicional (<a href>, recarga completa).
 *
 * - Externos / rutas de Drupal (p. ej. /historias, /user/login): <a href>.
 * - Rutas SPA de React: <Link> (cambia de vista sin volver a descargar el
 *   bundle ni el catálogo completo).
 * - Caso especial /search -> /search: el componente de búsqueda solo lee la
 *   query string al MONTARSE (useEffect con [] en components/.../Search). Si ya
 *   estamos en /search y navegamos a otra variante de /search con <Link>, el
 *   componente no se re-monta y el filtro nuevo no se aplicaría. Para no romper
 *   ese flujo forzamos recarga (<a>) solo en ese caso concreto; el resto de la
 *   navegación sigue siendo SPA.
 */
const SmartLink = ({ to, children, ...rest }) => {
  const location = useLocation();

  const spaNavigable = !isExternal(to) && isSpaRoute(to);

  if (spaNavigable) {
    const goingToSearch = pathnameOf(to).replace(/\/$/, '') === '/search';
    const alreadyOnSearch = location && location.pathname
      ? location.pathname.replace(/\/$/, '') === '/search'
      : false;

    // search -> search: recarga para que la búsqueda re-lea la query al montar.
    if (goingToSearch && alreadyOnSearch) {
      return (
        <a href={to} {...rest}>
          {children}
        </a>
      );
    }

    return (
      <Link to={to} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <a href={to} {...rest}>
      {children}
    </a>
  );
};

export default SmartLink;
